import { useState } from "react"
import { Grid } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FormikContextType, useFormikContext } from "formik"
import { BaseRow, Importer, ImporterField } from "react-csv-importer"
import "react-csv-importer/dist/index.css"
import { useAppSelector } from "../../../modules/store/storeHooks"
import { selectSelectedLocale } from "../../../modules/localization/localizationSelectors"

const useStyles = makeStyles()((theme) => ({
    container: {
        "& .CSVImporter_FileSelector": {
            border: `1px dashed ${theme.palette.tailwind.sky[700]}`,
            background: theme.palette.common.white,
            padding: "4.875rem",
        },
    },
    disabledImporter: {
        pointerEvents: "none",
        opacity: "0.5",
    },
}))

export type ImportCsvFileField = Readonly<{
    name: string
    label: string
    optional: boolean
}>

interface FormikImportCsvFileProps<T> {
    fields: ImportCsvFileField[]
    disabled?: boolean
}

export function FormikImportCsvFile<T>({ fields, disabled = false }: FormikImportCsvFileProps<T>) {
    const { classes, cx } = useStyles()
    const locale = useAppSelector(selectSelectedLocale)

    const { setFieldValue, submitForm } = useFormikContext<T>() as FormikContextType<T>
    const [importedData, setImportedData] = useState<BaseRow[]>([])

    const handleComplete = async () => {
        const csvContent = importedData.map((row) => Object.values(row).join(",")).join("\n")
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
        setFieldValue("file", blob) // Ensure "file" exists in T
        submitForm()
    }

    return (
        <Grid className={cx(classes.container, disabled && classes.disabledImporter)}>
            <Importer
                dataHandler={async (rows) => {
                    setImportedData((prev) => [...prev, ...rows])
                }}
                chunkSize={10000}
                defaultNoHeader={false}
                restartable={false}
                onStart={() => setImportedData([])}
                onComplete={handleComplete}
            >
                {fields.map((field) => (
                    <ImporterField key={field.name} name={field.name} label={field.label} optional={field.optional} />
                ))}
            </Importer>
        </Grid>
    )
}
